import React from "react";

// Hand-drawn squiggle / scribble
export const Squiggle = ({ color = "#FF5B3E", className = "", style }) => (
    <svg viewBox="0 0 120 30" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M2 18 Q 12 2, 22 18 T 42 18 T 62 18 T 82 18 T 102 18 T 118 14" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
);

// Dashed arc
export const DashedArc = ({ color = "#3D5BFF", className = "", style }) => (
    <svg viewBox="0 0 100 100" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M 10 80 A 50 50 0 0 1 90 30" stroke={color} strokeWidth="3" strokeLinecap="round" strokeDasharray="2 8" />
    </svg>
);

// Scribbled circle
export const ScribbleCircle = ({ color = "#FFD23F", className = "", style }) => (
    <svg viewBox="0 0 100 100" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M50 8 C 80 12, 92 30, 88 55 C 86 80, 60 92, 38 88 C 12 82, 6 56, 14 32 C 20 16, 38 6, 50 8 Z" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round"/>
        <path d="M52 14 C 78 18, 86 32, 84 52 C 82 74, 60 84, 42 80" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.6"/>
    </svg>
);

// Organic blob
export const Blob = ({ color = "#FF4FA3", className = "", style }) => (
    <svg viewBox="0 0 200 200" className={className} style={style} aria-hidden="true">
        <path fill={color} d="M44.4,-66.4C57.4,-58.5,67.5,-45.5,72.7,-31C77.9,-16.4,78.2,-0.3,73.6,13.6C69,27.5,59.5,39.2,48.4,49.8C37.4,60.5,24.7,70.1,9.7,73.5C-5.3,76.8,-22.7,73.9,-36.5,65.5C-50.3,57,-60.6,42.9,-67.7,27.3C-74.8,11.6,-78.7,-5.6,-74.4,-20.7C-70.1,-35.7,-57.6,-48.6,-43.6,-56.7C-29.6,-64.7,-14.8,-67.9,0.6,-68.8C16,-69.7,31.4,-68.4,44.4,-66.4Z" transform="translate(100 100)"/>
    </svg>
);

// Star burst
export const StarBurst = ({ color = "#7B3FE4", className = "", style }) => (
    <svg viewBox="0 0 100 100" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M50 4 L56 38 L92 28 L64 52 L92 76 L56 66 L50 96 L44 66 L8 76 L36 52 L8 28 L44 38 Z" fill={color}/>
    </svg>
);

// Dotted grid
export const DotGrid = ({ color = "#0A0A0A", className = "", style, rows = 4, cols = 4 }) => {
    const dots = [];
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) dots.push({ r, c });
    return (
        <svg viewBox={`0 0 ${cols * 10} ${rows * 10}`} className={className} style={style} aria-hidden="true">
            {dots.map((d, i) => <circle key={i} cx={d.c * 10 + 5} cy={d.r * 10 + 5} r="1.4" fill={color} />)}
        </svg>
    );
};

// Underline scribble
export const UnderlineScribble = ({ color = "#FF5B3E", className = "" }) => (
    <svg viewBox="0 0 200 14" fill="none" className={className} aria-hidden="true" preserveAspectRatio="none">
        <path d="M3 9 C 40 2, 80 12, 120 6 C 160 1, 180 8, 197 5" stroke={color} strokeWidth="4" strokeLinecap="round" fill="none"/>
    </svg>
);

// Hand-drawn arrow (curved)
export const CurvyArrow = ({ color = "#0A0A0A", className = "", style }) => (
    <svg viewBox="0 0 120 80" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M10 70 C 30 40, 60 20, 100 28" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M88 18 L 102 28 L 92 42" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
);

// Bengali character decorative
export const BnGlyph = ({ glyph = "অ", color = "#FF5B3E", className = "", style }) => (
    <span aria-hidden="true" className={className} style={{ fontFamily: '"Hind Siliguri", sans-serif', color, ...style }}>
        {glyph}
    </span>
);

// Tiny waveform icon (logo accent)
export const WaveIcon = ({ color = "#FF5B3E", className = "" }) => (
    <svg viewBox="0 0 24 16" className={className} aria-hidden="true">
        {[2, 6, 10, 14, 18, 22].map((x, i) => (
            <rect key={i} x={x - 1} y={8 - (i % 3 + 1) * 2} width="2" height={(i % 3 + 1) * 4} rx="1" fill={color}/>
        ))}
    </svg>
);

// Sound wave doodle
export const SoundDoodle = ({ color = "#FF5B3E", className = "", style }) => (
    <svg viewBox="0 0 160 60" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M5 30 Q 15 10, 25 30 T 45 30 T 65 30 T 85 30 T 105 30 T 125 30 T 145 30" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <circle cx="155" cy="30" r="3" fill={color}/>
    </svg>
);

// Speech bubble (hand-drawn)
export const SpeechBubble = ({ color = "#2EE8B5", className = "", style }) => (
    <svg viewBox="0 0 120 100" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M10 20 C 10 8, 18 4, 30 4 H 90 C 108 4, 114 14, 114 28 V 56 C 114 70, 106 78, 90 78 H 50 L 28 94 L 32 78 H 28 C 16 78, 10 70, 10 56 Z" fill={color} stroke="#0A0A0A" strokeWidth="2.5"/>
    </svg>
);

// Eye / accessibility icon (custom)
export const EyeOff = ({ color = "#3D5BFF", className = "", style }) => (
    <svg viewBox="0 0 80 60" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M5 30 C 20 10, 60 10, 75 30 C 60 50, 20 50, 5 30 Z" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round"/>
        <circle cx="40" cy="30" r="9" fill={color}/>
        <line x1="10" y1="55" x2="70" y2="5" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    </svg>
);

// Headphones (problem section)
export const Headphones = ({ color = "#FF4FA3", className = "", style }) => (
    <svg viewBox="0 0 100 90" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M14 60 V 50 C 14 26, 32 8, 50 8 C 68 8, 86 26, 86 50 V 60" stroke={color} strokeWidth="4" strokeLinecap="round" fill="none"/>
        <rect x="6" y="55" width="20" height="28" rx="6" fill={color}/>
        <rect x="74" y="55" width="20" height="28" rx="6" fill={color}/>
    </svg>
);

// Coffee mug
export const Mug = ({ color = "#7B3FE4", className = "", style }) => (
    <svg viewBox="0 0 100 90" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M16 28 H 70 V 70 C 70 80, 62 86, 50 86 H 36 C 24 86, 16 80, 16 70 Z" fill={color}/>
        <path d="M70 38 H 80 C 88 38, 90 46, 86 54 C 82 60, 74 60, 70 58" stroke={color} strokeWidth="4" fill="none" strokeLinecap="round"/>
        <path d="M30 14 Q 34 6, 38 14 M 46 12 Q 50 4, 54 12 M 62 14 Q 66 6, 70 14" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

// Stack of discs (caching)
export const Stack = ({ color = "#FFD23F", className = "", style }) => (
    <svg viewBox="0 0 100 80" fill="none" className={className} style={style} aria-hidden="true">
        <ellipse cx="50" cy="62" rx="36" ry="10" fill={color} stroke="#0A0A0A" strokeWidth="2"/>
        <ellipse cx="50" cy="46" rx="36" ry="10" fill={color} stroke="#0A0A0A" strokeWidth="2"/>
        <ellipse cx="50" cy="30" rx="36" ry="10" fill={color} stroke="#0A0A0A" strokeWidth="2"/>
        <ellipse cx="50" cy="14" rx="36" ry="10" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="2"/>
    </svg>
);

// Sparkle
export const Sparkle = ({ color = "#FF5B3E", className = "", style }) => (
    <svg viewBox="0 0 40 40" fill="none" className={className} style={style} aria-hidden="true">
        <path d="M20 4 L 24 16 L 36 20 L 24 24 L 20 36 L 16 24 L 4 20 L 16 16 Z" fill={color}/>
    </svg>
);
